import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import guidesApiService from 'src/api/services/guidesApiService';
import dataSearchApiService from 'src/api/services/dataSearchApiService';
import {
    DataSearchResult,
    DataSearchState,
    SearchByDocumentCriteria,
    SearchByPassengerCriteria,
    SearchByTicketCriteria,
    SearchType
} from 'src/pages/dataSearch/types';
import { AppState } from 'src/store/store';

const initialResult: DataSearchResult = {
    isReady: false,
    passengerIdentityConfirmation: null,
    passengerTypesConfirmation: [],
    quotas: [],
    events: [],
    ticketEdits: [],
    couponEdits: []
};

const initialState: DataSearchState = {
    isLoading: false,
    isExporting: false,
    searchType: undefined,
    criteria: null,
    result: initialResult,
    ticketTypes: [],
    requestId: undefined
};

export const searchByPassenger = createAsyncThunk(
    'data-search/by-passenger',
    async (request: SearchByPassengerCriteria) => {
        try {
            const data = await dataSearchApiService.searchByPassenger(request);
            return data;
        } catch (error) {
            if (error instanceof Error) {
                notification.error({ message: error.message });
                throw error;
            }
            throw new Error();
        }
    }
);

export const searchByTicket = createAsyncThunk('data-search/by-ticket', async (criteria: SearchByTicketCriteria) => {
    try {
        const data = await dataSearchApiService.searchByTicket(criteria);
        return data;
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
            throw error;
        }
        throw new Error();
    }
});

export const searchByDocument = createAsyncThunk(
    'data-search/by-document',
    async (request: SearchByDocumentCriteria) => {
        try {
            const data = await dataSearchApiService.searchByDocument(request);
            return data;
        } catch (error) {
            if (error instanceof Error) {
                notification.error({ message: error.message });
                throw error;
            }
            throw new Error();
        }
    }
);

export const loadTicketTypesAsync = createAsyncThunk('ticketTypes/load', async () => {
    try {
        const data = await guidesApiService.getTicketTypes();
        return data;
    } catch (error) {
        if (error instanceof Error) {
            notification.error({ message: error.message });
            throw error;
        }
        throw new Error();
    }
});

const dataSearchSlice = createSlice({
    name: 'dataSearch',
    initialState,
    reducers: {
        reset: () => initialState,
        searchTypeChanged: (state, action: PayloadAction<SearchType>) => {
            state.searchType = action.payload;
        },
        exportStarted: (state) => {
            state.isExporting = true;
        },
        exportFinished: (state) => {
            state.isExporting = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(searchByPassenger.pending, (state, action) => {
                state.isLoading = true;
                state.criteria = action.meta.arg;
                state.requestId = action.meta.requestId;
            })
            .addCase(searchByPassenger.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                    state.result = {
                        isReady: true,
                        passengerIdentityConfirmation: action.payload.passengerIdentityConfirmation,
                        passengerTypesConfirmation: action.payload.passengerTypesConfirmation,
                        quotas: action.payload.quotas,
                        events: action.payload.events,
                        ticketEdits: action.payload.ticketEdits?.map((x, index) => ({ ...x, key: index })),
                        couponEdits: action.payload.couponEdits?.map((x, index) => ({ ...x, key: index }))
                    };
                }
            })
            .addCase(searchByPassenger.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                }
            })
            .addCase(searchByTicket.pending, (state, action) => {
                state.isLoading = true;
                state.criteria = action.meta.arg;
                state.requestId = action.meta.requestId;
            })
            .addCase(searchByTicket.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                    state.result = {
                        ...initialResult,
                        isReady: true,
                        events: action.payload.events,
                        ticketEdits: action.payload.ticketEdits?.map((x, index) => ({ ...x, key: index })),
                        couponEdits: action.payload.couponEdits?.map((x, index) => ({ ...x, key: index }))
                    };
                }
            })
            .addCase(searchByTicket.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                }
            })
            .addCase(searchByDocument.pending, (state, action) => {
                state.isLoading = true;
                state.criteria = action.meta.arg;
                state.requestId = action.meta.requestId;
            })
            .addCase(searchByDocument.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                    state.result = {
                        ...initialResult,
                        isReady: true,
                        events: action.payload.events,
                        ticketEdits: action.payload.ticketEdits?.map((x, index) => ({ ...x, key: index })),
                        couponEdits: action.payload.couponEdits?.map((x, index) => ({ ...x, key: index }))
                    };
                }
            })
            .addCase(searchByDocument.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.isLoading = false;
                }
            })
            .addCase(loadTicketTypesAsync.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(loadTicketTypesAsync.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.ticketTypes = action.payload;
            })
            .addCase(loadTicketTypesAsync.rejected, (state) => {
                state.isLoading = false;
            });
    }
});

export const { reset, searchTypeChanged, exportStarted, exportFinished } = dataSearchSlice.actions;

const selectDataSearch = (state: AppState) => state.dataSearch;

export const selectIsLoading = (state: AppState) => selectDataSearch(state).isLoading;

export const selectIsExporting = (state: AppState) => selectDataSearch(state).isExporting;

export const selectDataSearchType = (state: AppState) => selectDataSearch(state).searchType;

export const selectSearchItems = (state: AppState) => selectDataSearch(state).result;

export const selectTicketTypes = (state: AppState) => selectDataSearch(state).ticketTypes;

export const selectDataSearchResult = (state: AppState) => selectDataSearch(state).result;

export const selectDataSearchResultIsReady = (state: AppState) => selectDataSearchResult(state).isReady;

export const selectDataSearchCriteria = (state: AppState) => selectDataSearch(state).criteria;

export default dataSearchSlice.reducer;
